/**
 * The group apply run (requirements F2.8–F2.13), driven from the item view:
 *
 *   resolve → group → items → subitems → updates → done
 *
 * Everything is resolved once up front (dynamic dates and {date} names against
 * today in the applier's timezone), then each stage is sent as batched aliased
 * mutations through lib/mondayBatch. A refused row is recorded with its reason
 * and the run goes on; a subitem whose item failed, or an update whose row
 * failed, is recorded as skipped. Nothing is rolled back.
 *
 * Updates are the exception to "the run goes on": when monday refuses
 * create_update for a missing scope (the install has not approved updates:write
 * yet), every other update would be refused the same way, so none of the rest
 * are sent and the result says so once (updatesRefused) instead of listing each.
 *
 * onProgress({ stage, done, total }) fires as stages advance; the result is
 * { groupId, counts, failures, updatesRefused }.
 */
import { fetchAccountPeople, knownMentionKeys, readApplier } from './accountPeople';
import { composeColumnValues } from './applyValues';
import { refusedFields } from './captureGroup';
import { describeApiErrors } from './useBoardColumns';
import { applyBaseDate, resolveColumnValue, resolveName } from './dynamicValues';
import { reasonOf, runBatches } from './mondayBatch';
import { serializeUpdate } from './updates';

export const LAST_GROUP_QUERY = `query ($boardId: [ID!]) { boards (ids: $boardId) { groups { id } } }`;
export const SUBITEMS_COLUMN_QUERY = `query ($boardId: [ID!]) { boards (ids: $boardId) { columns (types: [subtasks]) { id } } }`;

/**
 * Whether the board has a Subitems column (F2.12). Checked before reaching for
 * the subitems board so a group apply never bootstraps one with a dummy
 * subitem on the current item the way the subitem editor does.
 */
export async function hasSubitemsColumn(api, boardId) {
  const res = await api.api(SUBITEMS_COLUMN_QUERY, { variables: { boardId: [String(boardId)] } });
  const error = describeApiErrors(res);
  if (error) throw new Error(error);
  const columns = (res && res.data && res.data.boards && res.data.boards[0] && res.data.boards[0].columns) || [];
  return columns.length > 0;
}

/** The board's last group id, so the new group lands at the bottom; null when the board has none. */
export async function lastGroupId(api, boardId) {
  const res = await api.api(LAST_GROUP_QUERY, { variables: { boardId: [String(boardId)] } });
  const error = describeApiErrors(res);
  if (error) throw new Error(error);
  const groups = (res && res.data && res.data.boards && res.data.boards[0] && res.data.boards[0].groups) || [];
  return groups.length ? groups[groups.length - 1].id : null;
}

/** Who applies (for @Applier) and who exists (to drop mentions of people who left). Degrades, never blocks. */
/**
 * The account subdomain for the mention links, or null. Its own query:
 * reading `account` may need a scope the app lacks, and that must not cost the
 * applier. Without it the links are account-relative.
 */
export async function accountSlug(api) {
  try {
    const res = await api.api('query { account { slug } }');
    return (res && res.data && res.data.account && res.data.account.slug) || null;
  } catch (e) {
    return null;
  }
}

export async function buildUpdateContext(api, rows, applierLookup) {
  const ctx = { applier: null, known: null, slug: null };
  // me, or without me:read the context's user on the board (lib/accountPeople)
  ctx.applier = await readApplier(api, applierLookup).catch(() => null);
  if (!ctx.applier) console.error('Could not read the applying user for @Applier');
  ctx.slug = await accountSlug(api);
  const needsPeople = rows.some((r) => (r.updates || []).some((u) => (u.mentions || []).some((m) => m && m.type !== 'Applier')));
  if (needsPeople) {
    const people = await fetchAccountPeople(api);
    if (!people.error) ctx.known = knownMentionKeys(people);
  }
  return ctx;
}

const resolveCells = (cells, baseDate) => (cells || []).map((cv) => resolveColumnValue(cv, baseDate)).filter(Boolean);

/** Names and date rules fixed at both levels; keys kept so results map back. */
export function resolveContentForApply(content, baseDate) {
  return {
    groupTitle: resolveName(content.group.title, { date: baseDate }),
    groupColor: content.group.color || undefined,
    items: (content.items || []).map((it) => ({
      key: it.key,
      name: resolveName(it.name, { date: baseDate }),
      column_values: resolveCells(it.column_values, baseDate),
      updates: it.updates || [],
      subitems: (it.subitems || []).map((s) => ({
        key: s.key,
        name: resolveName(s.name, { date: baseDate }),
        column_values: resolveCells(s.column_values, baseDate),
        updates: s.updates || [],
      })),
    })),
  };
}

/** Adds up what monday reported per request (lib/mondayBatch onComplexity), per mutation. */
export function complexityTracker() {
  const summary = { requests: 0, points: 0, lowestLeft: null, byMutation: {} };
  const track = ({ ops, mutation, cost, after }) => {
    const m = summary.byMutation[mutation] || (summary.byMutation[mutation] = { requests: 0, ops: 0, points: 0, perOp: 0 });
    m.requests += 1;
    m.ops += ops;
    m.points += Number(cost) || 0;
    m.perOp = m.ops ? Math.round(m.points / m.ops) : 0;
    summary.requests += 1;
    summary.points += Number(cost) || 0;
    if (Number.isFinite(after)) summary.lowestLeft = summary.lowestLeft === null ? after : Math.min(summary.lowestLeft, after);
  };
  return { summary, track };
}

/**
 * applyGroupTemplate(api, {
 *   content, boardId,
 *   applierLookup,     // { userId, itemId, boardId }: @Applier when me cannot be read
 *   mapping: { itemIdMap, itemSkipIds, subIdMap, subSkipIds },
 *   withSubitems,      // false when the board has no subitems column and the user chose items only
 *   timeZone, onProgress, batch: { size, concurrency, sleep },
 * }) → { groupId, groupTitle, counts, failures, updatesRefused, complexity, created }
 *
 * `created` is { items: { key → id }, subitems: { key → id } } for the rows
 * that exist, which the files stage (lib/applyFiles) copies files into.
 *
 * `complexity` is what monday reported per request: per mutation the requests,
 * ops and points spent, plus the lowest budget left seen. Also logged as
 * [spt-apply], so a real apply shows what a group costs.
 */
export async function applyGroupTemplate(api, { content, boardId, applierLookup, mapping, withSubitems = true, timeZone, onProgress, batch = {} }) {
  const progress = (p) => onProgress && onProgress(p);
  const cost = complexityTracker();
  batch = { ...batch, onComplexity: cost.track };
  const baseDate = applyBaseDate(timeZone);
  const r = resolveContentForApply(content, baseDate);
  const failures = [];
  const fail = (level, name, reason) => failures.push({ level, name, reason });
  const subitemsPlanned = withSubitems ? r.items.reduce((n, it) => n + it.subitems.length, 0) : 0;
  let updatesRefused = false;
  const counts = {
    items: { planned: r.items.length, created: 0 },
    subitems: { planned: subitemsPlanned, created: 0 },
    updates: { planned: 0, posted: 0 },
  };

  // 1. the group, at the bottom of the board
  progress({ stage: 'group', done: 0, total: 1 });
  const after = await lastGroupId(api, boardId);
  const groupOps = [
    {
      key: 'group',
      mutation: 'create_group',
      args: {
        board_id: String(boardId),
        group_name: r.groupTitle,
        group_color: r.groupColor,
        ...(after ? { position_relative_method: 'after_at', relative_to: String(after) } : {}),
      },
    },
  ];
  const groupResult = (await runBatches(api, groupOps, batch)).get('group');
  if (!groupResult || !groupResult.data || !groupResult.data.id) {
    throw new Error(`Could not create the group: ${reasonOf(groupResult)}`);
  }
  const groupId = String(groupResult.data.id);
  progress({ stage: 'group', done: 1, total: 1, groupId });

  // 2. items
  const itemOps = r.items.map((it) => ({
    key: it.key,
    mutation: 'create_item',
    args: {
      board_id: String(boardId),
      group_id: groupId,
      item_name: it.name,
      column_values: JSON.stringify(composeColumnValues(it.column_values, mapping.itemIdMap, mapping.itemSkipIds)),
      create_labels_if_missing: true,
    },
  }));
  progress({ stage: 'items', done: 0, total: itemOps.length, groupId });
  const itemResults = await runBatches(api, itemOps, { ...batch, onProgress: (done, total) => progress({ stage: 'items', done, total, groupId }) });
  const itemIds = new Map();
  for (const it of r.items) {
    const res = itemResults.get(it.key);
    if (res && res.data && res.data.id) {
      itemIds.set(it.key, String(res.data.id));
      counts.items.created += 1;
    } else {
      fail('item', it.name, reasonOf(res));
    }
  }

  // 3. subitems, under the items that exist
  const subOps = [];
  const subIds = new Map();
  if (withSubitems) {
    for (const it of r.items) {
      const parentId = itemIds.get(it.key);
      for (const s of it.subitems) {
        if (!parentId) {
          fail('subitem', s.name, `skipped: its item "${it.name}" was not created`);
          continue;
        }
        subOps.push({
          key: s.key,
          mutation: 'create_subitem',
          args: {
            parent_item_id: parentId,
            item_name: s.name,
            column_values: JSON.stringify(composeColumnValues(s.column_values, mapping.subIdMap, mapping.subSkipIds)),
            create_labels_if_missing: true,
          },
        });
      }
    }
    progress({ stage: 'subitems', done: 0, total: subOps.length, groupId });
    const subResults = await runBatches(api, subOps, { ...batch, onProgress: (done, total) => progress({ stage: 'subitems', done, total, groupId }) });
    for (const it of r.items) {
      for (const s of it.subitems) {
        if (!itemIds.get(it.key)) continue; // already recorded as skipped
        const res = subResults.get(s.key);
        if (res && res.data && res.data.id) {
          subIds.set(s.key, String(res.data.id));
          counts.subitems.created += 1;
        } else {
          fail('subitem', s.name, reasonOf(res));
        }
      }
    }
  }

  // 4. updates, on every row that exists, in template order
  const rows = [];
  for (const it of r.items) {
    rows.push({ level: 'item', key: it.key, name: it.name, id: itemIds.get(it.key), updates: it.updates });
    if (withSubitems) for (const s of it.subitems) rows.push({ level: 'subitem', key: s.key, name: s.name, id: subIds.get(s.key), updates: s.updates });
  }
  counts.updates.planned = rows.reduce((n, row) => n + (row.updates || []).filter((u) => u.body && u.body.trim()).length, 0);
  if (counts.updates.planned > 0) {
    const ctx = await buildUpdateContext(api, rows, applierLookup);
    const updateOps = [];
    for (const row of rows) {
      (row.updates || []).forEach((u, i) => {
        const { body, mentions_list } = serializeUpdate(u, ctx);
        if (!body.trim()) return;
        if (!row.id) {
          fail('update', row.name, `skipped: its ${row.level} was not created`);
          return;
        }
        updateOps.push({
          key: `${row.key}:u${i}`,
          name: row.name,
          mutation: 'create_update',
          args: { item_id: row.id, body, mentions_list },
        });
      });
    }
    const total = updateOps.length;
    progress({ stage: 'updates', done: 0, total, groupId });
    // The first update goes alone: if monday refuses it for a missing scope, the
    // rest are not sent at all.
    const [first, ...rest] = updateOps;
    const updateResults = first ? await runBatches(api, [first], batch) : new Map();
    if (first) {
      updatesRefused = refusedFields(updateResults.get(first.key)).length > 0;
      progress({ stage: 'updates', done: updatesRefused ? total : 1, total, groupId });
    }
    if (!updatesRefused && rest.length) {
      const restResults = await runBatches(api, rest, {
        ...batch,
        onProgress: (done) => progress({ stage: 'updates', done: done + 1, total, groupId }),
      });
      for (const [k, v] of restResults) updateResults.set(k, v);
    }
    for (const op of updateOps) {
      const res = updateResults.get(op.key);
      if (!res) continue; // not sent: the scope was refused
      if (res.data && res.data.id) counts.updates.posted += 1;
      else if (refusedFields(res).length) updatesRefused = true;
      else fail('update', op.name, reasonOf(res));
    }
  }

  progress({ stage: 'done', done: 1, total: 1, groupId });
  console.info('[spt-apply] group apply complexity', cost.summary);
  // created: key → monday id per level, for the files stage (lib/applyFiles)
  const created = { items: Object.fromEntries(itemIds), subitems: Object.fromEntries(subIds) };
  return { groupId, groupTitle: r.groupTitle, counts, failures, updatesRefused, complexity: cost.summary, created };
}
